import { LoadingOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Form, Input } from 'antd';
import { has, isEmpty } from 'lodash';
import React, { useReducer, useRef } from 'react';
import { ReCAPTCHA } from 'react-google-recaptcha';
import { useDispatch } from 'react-redux';
import config from '../../../common/config/index';
import footerResource from '../../../locales/th/footer.json';
import {
  buildSubmitEnquiryVariables,
  useMutationSubmitEnquiry,
} from '../../../services/common';
import { updateModalVisible } from '../../../store/common/slice';
import { LeadFormWrapper } from './LeadForm.style';
import { sanitize } from '../../../common/helpers/utility/sanitizer';
import { useRouter } from 'next/router';

const FormItem = Form.Item;

interface SendLeadCaptureEmailResponse {
  sendLeadCaptureEmail: {
    status: string;
    message?: string;
  };
}
interface LeadFormProps {
  source: string;
}
interface AlertState {
  alertDisplay: boolean;
  alertType: 'success' | 'error';
  isShowLoading: boolean;
  codeMessageText: string | number;
}
const LeadForm: React.FC<LeadFormProps> = ({ source }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const router = useRouter();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [submitEnquiry] = useMutationSubmitEnquiry();
  const alertInitialState: AlertState = {
    alertDisplay: false,
    alertType: 'success',
    isShowLoading: false,
    codeMessageText: footerResource.leadCapture.form.alertSuccessMessage,
  };
  const reducer = (state: AlertState, action: string | number): AlertState => {
    if (action === 'success') {
      return {
        alertDisplay: true,
        alertType: 'success',
        isShowLoading: false,
        codeMessageText: footerResource.leadCapture.form.alertSuccessMessage,
      };
    } else if (action === 0) {
      return {
        alertDisplay: false,
        alertType: 'success',
        isShowLoading: false,
        codeMessageText: footerResource.leadCapture.form.alertSuccessMessage,
      };
    } else if (action === 1) {
      return {
        alertDisplay: false,
        alertType: 'success',
        isShowLoading: true,
        codeMessageText: footerResource.leadCapture.form.alertSuccessMessage,
      };
    } else if (action === 2) {
      return {
        alertDisplay: true,
        alertType: 'success',
        isShowLoading: true,
        codeMessageText: footerResource.leadCapture.form.notRobot,
      };
    } else {
      return {
        alertDisplay: true,
        alertType: 'error',
        isShowLoading: false,
        codeMessageText: action,
      };
    }
  };
  // const [state, dispatchReducer] = useReducer(
  //   reducer,
  //   alertInitialState,
  //   () => alertInitialState,
  // );
  const [state, dispatchReducer] = useReducer(reducer, alertInitialState);

  const onFinish = async (values: Record<string, string>) => {
    const recaptchaValue = recaptchaRef.current.getValue();
    if (isEmpty(recaptchaValue)) {
      dispatchReducer(2);
      return false;
    }

    dispatchReducer(1);
    const response = await submitEnquiry({
      variables: buildSubmitEnquiryVariables({
        name: sanitize(values.name),
        email: sanitize(values.email),
        phone: sanitize(values.phone),
        message: sanitize(isEmpty(values.message) ? '' : values.message),
        source: source,
        recaptcha: recaptchaValue,
      }),
    });

    if (has(response, 'data')) {
      const { sendLeadCaptureEmail } =
        response.data as SendLeadCaptureEmailResponse;
      if (sendLeadCaptureEmail.status === 'success') {
        dispatchReducer('success');
        form.resetFields();
      } else {
        dispatchReducer(sendLeadCaptureEmail.message);
      }
      recaptchaRef.current.reset();
    }
  };

  // const checkCheckBox = (_rule: unknown, value: boolean, callback: (message?: string) => void) => {
  //   const checkboxErrorMessage =
  //     footerResource.leadCapture.form.collectionStatementErrorMessage;
  //   value ? callBack() : callBack(checkboxErrorMessage);
  // };
  const checkCheckBox = (
    _rule: unknown,
    value: boolean,
    callback: (message?: string) => void,
  ) => {
    callback(
      value
        ? null
        : footerResource.leadCapture.form.collectionStatementErrorMessage,
    );
  };

  const onCloseAlert = () => {
    dispatchReducer(0);
  };

  const changeRouter = (slug: string) => {
    dispatch(updateModalVisible(false));
    // router to footer-page
    router.push(`/${slug}`).then(() => {
      window.scrollTo(0, 0);
    });
  };

  const getSubmitInfo = () => (
    <div className="submit-info">
      {footerResource.leadCapture.footnote.part1}
      <a
        className="other-item-link"
        onClick={() => changeRouter('terms-of-use')}
      >
        {footerResource.leadCapture.footnote.termsOfUse}
      </a>
      {footerResource.leadCapture.footnote.and}
      <a
        className="other-item-link"
        onClick={() => changeRouter('privacy-policy')}
      >
        {footerResource.leadCapture.footnote.privacyPolicy}
      </a>
      {footerResource.leadCapture.footnote.part2}
      <a
        className="other-item-link"
        onClick={() => changeRouter('cookie-policy')}
      >
        {footerResource.leadCapture.footnote.cookiePolicy}
      </a>
    </div>
  );

  return (
    <LeadFormWrapper>
      {state.alertDisplay && (
        <div className="alert">
          <Alert
            message={state.codeMessageText}
            type={state.alertType}
            closable
            showIcon
            closeText="OK"
            onClose={onCloseAlert}
          />
        </div>
      )}
      <Form form={form} onFinish={onFinish}>
        <div className="lead-form">
          <div className="message">
            <div className="text-info">
              {footerResource.leadCapture.form.message}
            </div>
            <FormItem name="message" rules={[{ required: false }]}>
              <Input.TextArea
                rows={7}
                placeholder={footerResource.leadCapture.form.messagePlaceholder}
              />
            </FormItem>
          </div>
          <div className="details">
            <div className="text-info">
              {footerResource.leadCapture.form.details}
            </div>
            <FormItem
              name="name"
              rules={[
                {
                  required: true,
                  message: footerResource.leadCapture.form.nameErrorMessage,
                },
              ]}
            >
              <Input
                placeholder={footerResource.leadCapture.form.namePlaceholder}
              />
            </FormItem>
            <FormItem
              name="email"
              rules={[
                {
                  type: 'email',
                  message: footerResource.leadCapture.form.emailErrorMessage,
                },
                {
                  required: true,
                  message: footerResource.leadCapture.form.emailErrorMessage,
                },
              ]}
            >
              <Input
                placeholder={footerResource.leadCapture.form.emailPlaceholder}
              />
            </FormItem>
            <FormItem
              name="phone"
              rules={[
                {
                  required: true,
                  message:
                    footerResource.leadCapture.form.phoneNumberErrorMessage,
                  pattern: new RegExp(
                    '^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\\s\\./0-9]*$',
                  ),
                },
              ]}
            >
              <Input
                placeholder={
                  footerResource.leadCapture.form.phoneNumberPlaceholder
                }
              />
            </FormItem>
            <ReCAPTCHA
              style={{ display: 'inline-block' }}
              ref={recaptchaRef}
              sitekey={config.reCaptchaSiteKey}
            />
            <FormItem
              className="check-box-wrapper"
              name="agreement"
              valuePropName="checked"
              rules={[{ validator: checkCheckBox }]}
            >
              <Checkbox className={'check-box-info'}>
                {footerResource.leadCapture.form.checkboxInfo}
              </Checkbox>
            </FormItem>
          </div>

          <div className="submit">
            {getSubmitInfo()}
            <div className="submit-button-wrapper">
              <Button className="submit-button" htmlType="submit">
                {state.isShowLoading && <LoadingOutlined />}
                {footerResource.leadCapture.form.submitButtonInfo}
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </LeadFormWrapper>
  );
};

export default LeadForm;
